import React, {useState} from 'react';
import {Popover, Stack, Text, Button, Group, Badge, Card} from '@mantine/core';
import {useSelectorWithRefresh} from '@/store/kysely_module';
import {IconExternalLink, IconMusic} from '@tabler/icons-react';
import {useNavigate} from 'react-router-dom';
import {useIsMobile} from '@/utils/user_agent';
import {navigateToPathForReason} from '@/modules/navigation/navigation_module';
import {ProfileMobileView} from '@/modules/music_sniper/components/profile_mobile/ProfileMobileView';
import {useAvatarImage} from '@/hooks/useAvatarImage';
import {PlayIcon} from '@/modules/audio_player/audio_player_module';
import {formatDuration} from '@/components/new_dashboard/utils/timeFormatting';
import {isAudioFile} from '@/utils/file_utils';
import {Avatar} from '@/components/Avatar';
import {MobileSheet} from '@/components/MobileSheet';

type UserPopoverProps = {
    userId: string;
    userName: string;
    children: React.ReactNode;
};

type UserPopoverInnerProps = {
    userId: string;
    userName: string;
    onClose: () => void;
};

const UserPopoverInner = ({userId, userName, onClose}: UserPopoverInnerProps) => {
    const navigate = useNavigate();
    const isMobile = useIsMobile();

    const [profileData] = useSelectorWithRefresh('getUserProfileData', {user_id: userId}, null, {workspace_id: userId});
    const {data: profilePictureUrl, refetch: refetchAvatar} = useAvatarImage(userId, profileData?.user?.profile_picture_file_id || null, profileData?.user?.updated_at || null);

    if (isMobile) {
        return (
            <MobileSheet
                isOpen={true}
                onClose={onClose}
                title={`@${userName}`}
            >
                <ProfileMobileView userId={userId} />
            </MobileSheet>
        );
    }

    const handleViewProfile = () => {
        navigateToPathForReason(navigate, 'user_popover_view_profile', `/users/${userId}/profile`);
        onClose();
    };

    if (profileData) {
        return (
            <Card p="xs" withBorder={false} style={{position: 'relative'}}>
                <Button
                    variant="subtle"
                    size="compact-xs"
                    rightSection={<IconExternalLink size={10} />}
                    onClick={handleViewProfile}
                    style={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        fontSize: '10px',
                        height: '20px',
                        padding: '0 6px',
                    }}
                >
                    View Profile
                </Button>
                <Stack gap="xs" align="center">
                    <Avatar
                        src={profilePictureUrl}
                        userName={profileData.user.name}
                        size="xl"
                        style={{marginTop: '24px'}}
                        onImageError={() => refetchAvatar()}
                    />
                    <Text fw={500} size="sm">{profileData.user.name}</Text>

                    {profileData.publicFiles.length > 0 && (
                        <Stack gap="xs" style={{width: '100%', alignSelf: 'stretch'}}>
                            <Group gap="xs">
                                <IconMusic size={14} />
                                <Text size="xs" fw={500}>
                                    Recent Tracks
                                </Text>
                                <Badge size="xs" variant="light">
                                    {profileData.publicFiles.length}
                                </Badge>
                            </Group>
                            <Stack gap={4}>
                                {profileData.publicFiles.slice(0, 3).map((file) => {
                                    const duration = file.attributes?.duration;
                                    return (
                                        <Group key={file.id} gap="xs" wrap="nowrap">
                                            {isAudioFile(file.name) && <PlayIcon fileInfo={file} size={12} />}
                                            <Text size="xs" style={{flex: 1}} lineClamp={1}>
                                                {file.name}
                                            </Text>
                                            {duration && (
                                                <Text size="xs" c="dimmed">
                                                    {formatDuration(Number(duration))}
                                                </Text>
                                            )}
                                        </Group>
                                    );
                                })}
                                {profileData.publicFiles.length > 3 && (
                                    <Text size="xs" c="dimmed">
                                        +{profileData.publicFiles.length - 3} more tracks
                                    </Text>
                                )}
                            </Stack>
                        </Stack>
                    )}
                </Stack>
            </Card>
        );
    }

    return (
        <Stack gap="xs" p="xs" align="center" style={{position: 'relative'}}>
            <Button
                variant="subtle"
                size="compact-xs"
                rightSection={<IconExternalLink size={10} />}
                onClick={handleViewProfile}
                style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    fontSize: '10px',
                    height: '20px',
                    padding: '0 6px',
                }}
            >
                View
            </Button>
            <Avatar
                userName={userName}
                size="xl"
                style={{marginTop: '24px'}}
            />
            <Text fw={500} size="sm">{userName}</Text>
        </Stack>
    );
};

export const UserPopover = ({userId, userName, children}: UserPopoverProps) => {
    const [opened, setOpened] = useState(false);
    const isMobile = useIsMobile();

    const handleClick = () => {
        setOpened(!opened);
    };

    if (isMobile) {
        return (
            <>
                <span
                    onClick={handleClick}
                    style={{cursor: 'pointer', display: 'inline-block'}}
                >
                    {children}
                </span>
                {opened && (
                    <UserPopoverInner
                        userId={userId}
                        userName={userName}
                        onClose={() => setOpened(false)}
                    />
                )}
            </>
        );
    }

    return (
        <Popover
            width={260}
            position="bottom-start"
            withArrow
            shadow="md"
            opened={opened}
            onChange={setOpened}
        >
            <Popover.Target>
                <span
                    onClick={handleClick}
                    style={{cursor: 'pointer', display: 'inline-block'}}
                >
                    {children}
                </span>
            </Popover.Target>
            <Popover.Dropdown>
                {opened && (
                    <UserPopoverInner
                        userId={userId}
                        userName={userName}
                        onClose={() => setOpened(false)}
                    />
                )}
            </Popover.Dropdown>
        </Popover>
    );
};
